<template>
    <div class="gen-ai-usage-page">
        <div class="dashboard-row">
            <p class="section-title">Usage</p>
            <div class="dashboard-card-wrapper">
                <div class="dashboard-card no-sub-detail">
                    <div class="card-headline">
                        <p class="card-title">Total Request</p>
                        <img src="@/assets/dashboard-icon/total-request.svg" alt="total request icon">
                    </div>
                    <div>
                        <div class="card-detail">
                            <div class="card-main-detail">
                                <p>{{formatNumber(generativeAIUsageStat.totalRequest)}} </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="dashboard-card">
                    <div class="card-headline">
                        <p class="card-title">Total Input Token</p>
                        <img src="@/assets/dashboard-icon/input-token.svg" alt="input token icon">
                    </div>
                    <div>
                        <div class="card-detail">
                            <div class="card-main-detail">
                                <p>{{formatNumber(generativeAIUsageStat.totalInputToken)}} </p>
                            </div>
                            <div>
                                <p class="card-sub-detail">avg {{ formatNumber(generativeAIUsageStat.avgInputTokenPerRequest) }} token/request</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="dashboard-card">
                    <div class="card-headline">
                        <p class="card-title">Total Output Token</p>
                        <img src="@/assets/dashboard-icon/output-token.svg" alt="output token icon">
                    </div>
                    <div>
                        <div class="card-detail">
                            <div class="card-main-detail">
                                <p>{{formatNumber(generativeAIUsageStat.totalOutputToken)}} </p>
                            </div>
                            <div>
                                <p class="card-sub-detail">avg. {{ formatNumber(generativeAIUsageStat.avgOutputTokenPerRequest) }} token/request</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="dashboard-card no-sub-detail">
                    <div class="card-headline">
                        <p class="card-title">Avg. Response Time</p>
                        <img src="@/assets/dashboard-icon/response-time.svg" alt="avg response time icon">
                    </div>
                    <div>
                        <div class="card-detail">
                            <div class="card-main-detail">
                                <p>{{ formatNumber(generativeAIUsageStat.avgResponseTime/1000) }} <span class="text-unit">seconds</span></p>
                            </div>
                        </div>
                        </div>
                </div>
            </div>
            <div class="dashboard-row">
                <GenAIUsageTrendWidget
                  :usage-history="generativeAIUsageHistory"
                  :criteria="criteria"
                  @on-export="exportGenerativeAIUsageFile"
                />
            </div>
            <div class="dashboard-row">
              <GenAIUsagePromptLogsTableWidget
                ref="promptLogsTableRef"
                :is-loading="isLoadingPromptLog"
                :sort-options="promptLogSortOptions"
                :selected-sort-option="selectedPromptLogSortOption"
                :prompt-log-list="generativeAIUsagePromptLogList"
                @on-select-sort-option="onSelectPromptLogSortOption"
                @on-scroll-to-load="onScrollToLoad"
              />
            </div>
        </div>
    </div>
</template>
<script>
import numeral from 'numeral';
import { mapGetters, mapActions } from 'vuex';

import alice from '../../alice-api';
import { getFilenameFromContentDispositionHelper, downloadFileHelper } from '../../utiles/exportFileHelper';

import GenAIUsageTrendWidget from '../DashboardWidgets/GenAIUsageTrendWidget.vue';
import GenAIUsagePromptLogsTableWidget from '../DashboardWidgets/GenAIUsagePromptLogsTableWidget.vue';

const PROMPT_LOG_SORT_OPTION_LIST = [
  {
    label: 'Date',
    value: 'created_at',
  },
  {
    label: 'Input Tokens Used',
    value: 'input_token',
  },
  {
    label: 'Output Tokens Used',
    value: 'output_token',
  },
  {
    label: 'Response Time',
    value: 'response_time',
  },
];

export default {
  name: 'GenAIUsageDashboard',
  components: {
    GenAIUsageTrendWidget,
    GenAIUsagePromptLogsTableWidget,
  },
  props: {
    criteria: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      generativeAIUsageStat: {},
      generativeAIUsageHistory: null,
      isLoadingPromptLog: false,
      promptLogSortOptions: PROMPT_LOG_SORT_OPTION_LIST,
      selectedPromptLogSortOption: PROMPT_LOG_SORT_OPTION_LIST[0],
      generativeAIUsagePromptLogList: [],
      generativeAIUsagePromptLogCursor: null,
      isLastPromptLog: false,
      paginationSize: 20,
    };
  },
  watch: {
    criteria: {
      handler: 'init',
      immediate: true,
    },
  },
  methods: {
    ...mapActions({
      loadGenerativeAIUsageData: 'dashboard/loadGenerativeAIUsageData',
      loadGenerativeAIUsagePromptlogData: 'dashboard/loadGenerativeAIUsagePromptlogData',
      resetGenerativeAIUsagePromptlogData: 'dashboard/resetGenerativeAIUsagePromptlogData',
    }),
    async init() {
      await this.loadGenerativeAIUsageData({
        criteria: this.criteria,
      });
      this.generativeAIUsageStat = this.generativeAIUsageData.generativeAIUsageStat;
      this.generativeAIUsageHistory = this.generativeAIUsageData.generativeAIUsageHistory;

      [this.selectedPromptLogSortOption] = PROMPT_LOG_SORT_OPTION_LIST;
      await this.resetPromptLogTable();
    },
    async resetPromptLogTable() {
      await this.resetGenerativeAIUsagePromptlogData();

      this.generativeAIUsagePromptLogList = [];
      this.generativeAIUsagePromptLogCursor = null;
      this.isLastPromptLog = false;

      await this.$refs.promptLogsTableRef.resetPromptLogTableScrollPosition();
      this.onLoadPromptLog();
    },
    async onLoadPromptLog(cursor) {
      const param = {
        criteria: this.criteria,
        sortField: this.selectedPromptLogSortOption.value ? this.selectedPromptLogSortOption.value : 'created_at',
        size: this.paginationSize
      };

      if (cursor && cursor.cursorId && cursor.cursorSort) {
        param.cursorSort = cursor.cursorSort;
        param.cursorId = cursor.cursorId;
      }

      await this.loadGenerativeAIUsagePromptlogData(param);

      if (!this.generativeAIUsagePromptLogData) return;

      this.generativeAIUsagePromptLogList = this.generativeAIUsagePromptLogData.data || [];
      this.generativeAIUsagePromptLogCursor = this.generativeAIUsagePromptLogData.cursor || null;
      this.isLastPromptLog = this.generativeAIUsagePromptLogData.last || false;
    },
    async onSelectPromptLogSortOption(sortOption) {
      this.selectedPromptLogSortOption = sortOption;
      this.resetPromptLogTable();
    },
    async onScrollToLoad(state) {
      if (this.isLastPromptLog) {
        state.complete();
        return;
      }
      try {
        await this.onLoadPromptLog(this.generativeAIUsagePromptLogCursor);
        state.loaded();
      } catch (error) {
        state.error();
        console.error('Error loading more files', error);
      }
    },
    async exportGenerativeAIUsageFile() {
      if (!this.criteria) return;
      const response = await alice.exportGenerativeAIUsage(this.criteria);
      const filename = getFilenameFromContentDispositionHelper(response);
      downloadFileHelper(response.data, filename);
    },
    formatNumber(value) {
      return numeral(value).format('0.[0]a');
    }
  },
  computed: {
    ...mapGetters({
      generativeAIUsageData: 'dashboard/generativeAIUsageData',
      generativeAIUsagePromptLogData: 'dashboard/generativeAIUsagePromptLogData',
    }),
  }
};
</script>
<style lang="scss" scoped>
.gen-ai-usage-page{
    padding: 30px 40px;
    color: #4B5178;
    .section-title{
      font-size: 16px;
      font-weight: 500;
      line-height: 19.2px;
      text-align: left;
      color: #000000;
      margin-bottom: 24px;
    }
    .dashboard-row{
        margin-bottom: 24px;
        .dashboard-card-wrapper{
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 24px;
          min-height: 140px;
          .dashboard-card{
            background-color: #FFFFFF;
            border-radius: 16px;
            padding: 16px 18.5px 20px 20px;
            font-weight: 500;
            .card-headline{
              display: flex;
              justify-content: space-between;
              align-items: center;
              font-size: 14px;
              line-height: 21px;
              text-align: left;
              color : #646A8C;
              padding-bottom: 12px;
              p {
                margin-bottom: 0px;
              }
            }
            .card-detail{
              p {
                margin-bottom: 0px;
              }
              .card-main-detail{
                display: flex;
                align-items: center;
                font-size: 26px;
                line-height: 31.2px;
                color: #0D173C;
                padding-bottom: 4px;
              }
              .card-sub-detail{
                font-size: 14px;
                font-weight: 400;
                line-height: 21px;
                color: #878A99;
              }
              .no-chagnge-text{
                font-size: 14px;
                font-weight: 400;
                line-height: 21px;
                color: #878A99;
                padding-left: 8px;
              }
              .text-unit{
                font-size: 14px;
                font-weight: 400;
                line-height: 21px;
              }
            }
          }
          .dashboard-card{
            display: flex;
            flex-direction: column;
            justify-content: center;
          }
          .dashboard-card.no-sub-detail{
            display: flex;
            justify-content: flex-start;
          }
        }
      }
}
</style>
